import
  Image
from 'next/image';

import {
  BRAND_GRADIENT,
  BRAND_GRADIENT_FROM,
  BRAND_GRADIENT_TO
} from '@repo/brand';

import
  PrimaryLinkButton
from './primary-button';

import {
  PrimaryButton as PrimaryClickButton
} from './app-button';

import
  OutlineButton
from './outline-button';

interface ErrorShellLinkAction {
  href: string
  label: string
}

interface ErrorShellButtonAction {
  onClick: () => void
  label: string
}

interface ErrorShellProps {
  // Where the logo links to - the same across today's callers, but kept as a
  // prop rather than hardcoded so each app's not-found/error page stays free
  // to point it elsewhere without touching this shared shell.
  logoHref: string
  heading: string
  // Tailwind text-size class for the heading - "404" and "Oops" read best at
  // different sizes, so callers set this explicitly rather than the shell
  // guessing from string length.
  headingSize: string
  subheading: string
  message: string
  // Link-style ({ href, label }) for not-found's "Go to Dashboard", or
  // button-style ({ onClick, label }) for error.tsx's "Try again", which
  // needs the reset() callback from Next's error-boundary props.
  primaryAction: ErrorShellLinkAction | ErrorShellButtonAction
  secondaryAction: ErrorShellLinkAction
}

// Full-page centered card shown by every app/{not-found,error}.tsx - logo,
// heading, message, and one or two CTAs over the shared brand-gradient
// blobs. Previously hand-copied per app with drifting dark-mode classes and
// a hardcoded blob color that had drifted from BRAND_GRADIENT's real stops.
export default function ErrorShell({
  logoHref, heading, headingSize, subheading, message, primaryAction, secondaryAction
}: ErrorShellProps) {

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center px-4 py-10"
      style={{
        background: 'var(--page-gradient)'
      }}
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div
          className="absolute -top-32 -left-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: BRAND_GRADIENT_FROM
          }}
        />
        <div
          className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{
            background: BRAND_GRADIENT_TO
          }}
        />
      </div>

      <div className="relative w-full max-w-sm text-center">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <a href={logoHref}>
            <Image unoptimized src="/logo.png" alt="Elite Scholars" width={120} height={60} className="h-14 w-auto dark:brightness-0 dark:invert" priority />
          </a>
        </div>

        {/* Card */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 px-8 py-10">
          <h1
            className={`${ headingSize } font-bold leading-none mb-4 select-none`}
            style={{
              background: BRAND_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {heading}
          </h1>

          <p className="text-lg font-light text-[#111111] dark:text-white mb-2">{subheading}</p>
          <p className="text-sm text-gray-400 mb-8">
            {message}
          </p>

          <div className="flex flex-col gap-3">
            {'href' in primaryAction ? (
              <PrimaryLinkButton href={primaryAction.href} external block className="py-3">
                {primaryAction.label}
              </PrimaryLinkButton>
            ) : (
              <PrimaryClickButton onClick={primaryAction.onClick}>
                {primaryAction.label}
              </PrimaryClickButton>
            )}
            <OutlineButton href={secondaryAction.href} external block className="py-3">
              {secondaryAction.label}
            </OutlineButton>
          </div>
        </div>
      </div>
    </div>
  );
}
